import React, { useContext } from "react";

import { Preview } from "@creatomate/preview";
import { classnames, Dropdown, Icon } from "@culturehq/components";

import { Trans } from "app/utils";

import { useTemplateBuilder } from "components/admin/templates/context/TemplateBuilderContext";
import { getCreatomateMediaData } from "../../utils/creatomate/utils";
import { CreatomateContext } from "../../contexts/VideoStudioContext";

interface TemplateActionButtonsProps {
  preview: Preview;
  onClose: () => void;
}

const TemplateActionButtons: React.FC<TemplateActionButtonsProps> = ({ preview, onClose }) => {
  const { state: { template }, handleSave, restartContextValues } = useTemplateBuilder();
  const { data, restartData, handleAutosaveDraft } = useContext(CreatomateContext);
  const { selectedThumbnail, selectedMediaVersion, onThirdPartyMediasReload } = data;

  const handlePause = async (): Promise<void> => {
    await handleSave({
      status: "draft",
      creatomateJson: preview.state
    });
    restartData();
    restartContextValues();
    if (onThirdPartyMediasReload) onThirdPartyMediasReload(true);
    preview.dispose();
    onClose();
  };

  const handleSetActive = async (): Promise<void> => {
    await handleSave({
      status: "active",
      creatomateJson: getCreatomateMediaData(preview, false),
      thumbnail: selectedThumbnail || selectedMediaVersion?.thumbnail
    });
    restartData();
    restartContextValues();
    if (onThirdPartyMediasReload) onThirdPartyMediasReload(true);
    preview.dispose();
    onClose();
  };

  const handleSaveDraft = async (): Promise<void> => {
    if (template) {
      await handleSave({}); // empty object just to trigger a title update
      await handleAutosaveDraft({ creatomateJson: preview.state });
      restartData();
      restartContextValues();
      if (onThirdPartyMediasReload) onThirdPartyMediasReload(true);
      preview.dispose();
      onClose();
    } else {
      // we load the source because we do not need to add multipart values for a draft template
      await handleSave({
        status: "draft",
        creatomateJson: preview.state
      });
      restartData();
      restartContextValues();
      if (onThirdPartyMediasReload) onThirdPartyMediasReload(true);
      preview.dispose();
      onClose();
    }
  };

  const active = template?.status === "active";

  const handleOptionSelect = async (selectedValue: string) => {
    switch (selectedValue) {
      case "save_draft":
        await handleSaveDraft();
        break;
      case "save_active":
        await handleSetActive();
        break;
      case "pause":
        await handlePause();
        break;
      default:
        break;
    }
  };
  return (
    <>
      <div>
        <Dropdown onChange={handleOptionSelect} selected="">
          <Dropdown.Button className={classnames("Download-Btn", "btn-dropdown")}>
            <Icon icon="checkmark" />
            <span className="Download-Text"><Trans>Save</Trans></span>
          </Dropdown.Button>
          <Dropdown.ListBox className="Download-Options">
            {
              active && (
                <Dropdown.Option className="Download-Option" value="pause">
                  <Trans>Pause</Trans>
                </Dropdown.Option>
              )
            }
            <Dropdown.Option className="Download-Option" value="save_draft">
              <Trans>Save draft</Trans>
            </Dropdown.Option>
            <Dropdown.Option className="Download-Option" value="save_active">
              <Trans>{active ? "Update template" : "Set active"}</Trans>
            </Dropdown.Option>
          </Dropdown.ListBox>
        </Dropdown>
      </div>
    </>
  );
};

export default TemplateActionButtons;
